import { IslandType, ObstructionType } from '@rta/rta-blueprint-library';
import type { Island, Kitchen, Obstruction, Panel } from '@rta/rta-blueprint-library';

import { getIslandSuffix } from './get-island-suffix';
import { getSortedIslands } from './get-sorted-islands';

export type GetKitchenLocationLabelParams = {
	kitchen: Kitchen;
};

/**
 * Auto-derives the "Kitchen Location" label from the kitchen's obstructions.
 *
 * Rules (from scanning ~9k blueprint PDFs):
 *   - No obstructions → "Freestanding"
 *   - One obstruction type → "Against {Type} – {sides}"
 *   - Mixed types → "Against {Type1} and {Type2} – {sides}"
 *   - Multi-island → per-island breakdown on separate lines
 *
 * Obstruction types are future-proofed: the label is derived from the
 * ObstructionType enum value, not hardcoded per type.
 */

/** Maps ObstructionType enum values to human-readable singular/plural labels. */
const TYPE_LABELS: Record<string, { singular: string; plural: string }> = {
	[ObstructionType.Standard]: { singular: 'Wall', plural: 'Walls' },
	[ObstructionType.Pillar]: { singular: 'Pillar', plural: 'Pillars' },
	[ObstructionType.Column]: { singular: 'Column', plural: 'Columns' },
	[ObstructionType.Railing]: { singular: 'Railing', plural: 'Railings' }
};

const getTypeLabel = (type: string, count: number): string => {
	const labels = TYPE_LABELS[type];
	if (labels) return count > 1 ? labels.plural : labels.singular;
	// Future-proof: capitalize the enum value for unknown types.
	const raw = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
	return count > 1 ? `${raw}s` : raw;
};

type ObstructionSide = 'front' | 'left' | 'back' | 'right' | 'back left' | 'back right';

type IslandObstructionInfo = {
	island: Island;
	sides: Array<ObstructionSide>;
	types: Array<string>;
};

const getPanelSide = (panel: Panel | null, island: Island): ObstructionSide | null => {
	if (!panel) return null;
	if (panel.isFrontPanel) return 'front';
	if (panel.isBackPanel) {
		// Corner islands have two back panels; word each by its own label ("back left" / "back right").
		if (island.type === IslandType.Corner) {
			return panel.label === 'back right' ? 'back right' : 'back left';
		}
		return 'back';
	}
	if (panel.isLeftPanel) return 'left';
	if (panel.isRightPanel) return 'right';
	return null;
};

/**
 * For each island, find which sides have obstructions. Connections can go in
 * either direction (island→obstruction OR obstruction→island), so we check
 * both the island's connections AND each obstruction's connections.
 */
const getIslandObstructionInfo = (kitchen: Kitchen): Array<IslandObstructionInfo> => {
	const islandIds = new Set(kitchen.design.islands.children.map((i: Island) => i.id));
	const obstructionIds = new Set(kitchen.design.obstructions.children.map((o: Obstruction) => o.id));
	const resultMap = new Map<string, IslandObstructionInfo>();

	const getOrCreate = (island: Island): IslandObstructionInfo => {
		let info = resultMap.get(island.id);
		if (!info) {
			info = { island, sides: [], types: [] };
			resultMap.set(island.id, info);
		}
		return info;
	};

	const addSide = (info: IslandObstructionInfo, side: ObstructionSide | null, obstructionType: string): void => {
		if (side && !info.sides.includes(side)) info.sides.push(side);
		if (obstructionType && !info.types.includes(obstructionType)) info.types.push(obstructionType);
	};

	// Direction 1: walk each island's connections looking for obstruction targets.
	kitchen.design.islands.children.forEach((island: Island) => {
		island.connections.getSourceConnections().forEach((conn) => {
			if (obstructionIds.has(conn.targetIslandId)) {
				const info = getOrCreate(island);
				const side = getPanelSide(conn.sourcePanel, island);
				const obstruction = kitchen.design.obstructions.findById(conn.targetIslandId);
				addSide(info, side, obstruction?.type);
			}
		});
	});

	// Direction 2: walk each obstruction's connections looking for island targets.
	// When an obstruction is SOURCE connecting to an island TARGET, the sourcePanel
	// is on the obstruction. The island's connected panel is the one with a target
	// connection back to the obstruction.
	kitchen.design.obstructions.children.forEach((obstruction: Obstruction) => {
		obstruction.connections.getSourceConnections().forEach((conn) => {
			if (islandIds.has(conn.targetIslandId)) {
				const island = kitchen.design.islands.findById(conn.targetIslandId);
				if (!island) return;
				const info = getOrCreate(island);

				// Find which island panel connects back to this obstruction.
				const targetConn = island.connections.children.find(
					(c) => c.targetIslandId === obstruction.id || (c.sourceIsland?.id === obstruction.id)
				);
				const islandPanel = targetConn?.sourcePanel ?? null;
				const side = getPanelSide(islandPanel, island);
				addSide(info, side, obstruction.type);
			}
		});

		// Also check obstruction's target connections (island is SOURCE → obstruction).
		obstruction.connections.getTargetConnections().forEach((conn) => {
			const sourceEntity = conn.sourceIsland;
			if (sourceEntity && islandIds.has(sourceEntity.id)) {
				const island = sourceEntity as Island;
				const info = getOrCreate(island);
				const side = getPanelSide(conn.sourcePanel, island);
				addSide(info, side, obstruction.type);
			}
		});
	});

	return Array.from(resultMap.values());
};

const isPhraseSide = (side: ObstructionSide): boolean => side.includes(' ');

const SIDE_ORDER: Array<ObstructionSide> = ['front', 'left', 'back', 'back left', 'back right', 'right'];

const formatSideList = (sides: Array<ObstructionSide>): string => {
	const unique = [...new Set(sides)].sort((a, b) => SIDE_ORDER.indexOf(a) - SIDE_ORDER.indexOf(b));
	if (unique.length === 0) return '';
	const allPhrases = unique.every(isPhraseSide);
	if (unique.length === 1) return isPhraseSide(unique[0]) ? unique[0] : `${unique[0]} side`;
	if (unique.length === 2) return `${unique[0]} and ${unique[1]}${allPhrases ? '' : ' sides'}`;
	return `${unique.slice(0, -1).join(', ')}, and ${unique[unique.length - 1]}${allPhrases ? '' : ' sides'}`;
};

const joinWithAnd = (items: Array<string>): string => {
	if (items.length === 0) return '';
	if (items.length === 1) return items[0];
	if (items.length === 2) return `${items[0]} and ${items[1]}`;
	return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export const getKitchenLocationLabel = (params: GetKitchenLocationLabelParams): string => {
	const { kitchen } = params;
	const obstructions = kitchen.design.obstructions.children;

	if (obstructions.length === 0) return 'Freestanding';

	const unsortedInfos = getIslandObstructionInfo(kitchen);
	if (unsortedInfos.length === 0) return 'Freestanding';

	// Sort by island letter (spatial left→right) so the per-island breakdown
	// reads A, B, C, D regardless of when obstructions were connected.
	const spatialOrder = getSortedIslands(kitchen.design.islands.children);
	const infos = [...unsortedInfos].sort((a, b) => {
		const ai = spatialOrder.findIndex((i) => i.id === a.island.id);
		const bi = spatialOrder.findIndex((i) => i.id === b.island.id);
		return ai - bi;
	});

	// Collect all obstruction types across all islands.
	const allTypes: Array<string> = [];
	const typeCounts: Record<string, number> = {};
	obstructions.forEach((o: Obstruction) => {
		const t = o.type;
		typeCounts[t] = (typeCounts[t] ?? 0) + 1;
		if (!allTypes.includes(t)) allTypes.push(t);
	});

	// Build the "Against {Type(s)}" prefix.
	const typeLabels = allTypes.map((t) => getTypeLabel(t, typeCounts[t]));
	const prefix = `Against ${joinWithAnd(typeLabels)}`;

	const isMultiIsland = kitchen.design.islands.children.length > 1;

	if (!isMultiIsland) {
		// Single island: "Against Wall – back and left sides"
		const sides = infos[0]?.sides ?? [];
		if (sides.length === 0) return prefix;
		return `${prefix} – ${formatSideList(sides)}`;
	}

	// Multi-island: main line + per-island breakdown.
	const perIslandLines = infos.map((info) => {
		const letter = getIslandSuffix({ island: info.island, kitchen });
		const islandLabel = letter ? `Island ${letter}` : info.island.description.split(' | ')[0];
		const sideStr = info.sides.length > 0 ? ` – ${formatSideList(info.sides)}` : '';
		return `${islandLabel}${sideStr}`;
	});

	return `${prefix}\n${perIslandLines.join('\n')}`;
};
